import {
  ChangeEventHandler,
  type FC,
  KeyboardEventHandler,
  type ReactNode,
  useCallback,
} from 'react';
import { useLibraryTranslation } from '../../../i18n';
import { QStack } from '../../../QLayouts';
import { QActiveFiltersView } from '../../../QMolecules';
import { QInput } from '../../../QAtoms';
import { FilterPopover } from '../Filtering/popover';
import { useFiltering } from '../Filtering';
import { FilterCustomView } from '../Filtering/custom';
import { useTableInstrumentationMaybe } from '../Instrumentation/context';

export type FilterProps = {
  customFilter?: boolean;
  children?: ReactNode;
};

export const Filters: FC<FilterProps> = ({
  customFilter = false,
  children,
}) => {
  const { definitions, searchDisabled } = useFiltering();

  // If there are available filters, build logic to display custom filter or filters in modal
  const filtersAvailable = Object.keys(definitions).length > 0;
  const showFilterButton = filtersAvailable && !customFilter;
  const showCustomFilter = filtersAvailable && customFilter;

  return (
    <>
      <QStack marginY="8px">
        <QStack direction="row">
          {showCustomFilter && <FilterCustomView>{children}</FilterCustomView>}
          {showFilterButton && <FilterPopover>{children}</FilterPopover>}
          {!searchDisabled && <SearchInput />}
        </QStack>
        {showFilterButton && <QActiveFiltersView />}
      </QStack>
    </>
  );
};

const SearchInput: FC = () => {
  const { t } = useLibraryTranslation();
  const { searchTerm, setSearchTerm } = useFiltering();
  const instrumentation = useTableInstrumentationMaybe();

  const onSearchTermChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => setSearchTerm(e.target.value),
    [setSearchTerm],
  );

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      if (e.code === 'Enter') {
        instrumentation?.markInteraction('search');
        setSearchTerm(e.currentTarget.value, Date.now());
      }
    },
    [setSearchTerm, instrumentation],
  );

  return (
    <QInput
      maxWidth="400px"
      iconLeftName="Search"
      placeholder={t('table.search')}
      isClearable
      value={searchTerm}
      onChange={onSearchTermChange}
      onKeyDown={handleKeyDown}
      data-cy="table-search-input"
    />
  );
};
